import React, { useState, useEffect } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const FeaturedCarousel = ({ featuredProjects, onImageClick, onBookService }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || featuredProjects?.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide(prev => 
        prev === featuredProjects?.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, featuredProjects?.length]);

  const handlePrevSlide = () => {
    setCurrentSlide(prev => 
      prev === 0 ? featuredProjects?.length - 1 : prev - 1
    );
    setIsAutoPlaying(false);
  };

  const handleNextSlide = () => {
    setCurrentSlide(prev => 
      prev === featuredProjects?.length - 1 ? 0 : prev + 1
    );
    setIsAutoPlaying(false);
  };

  const handleSlideClick = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const handleViewProject = (project) => {
    onImageClick(project, 0);
  };

  const handleBookService = (serviceType) => {
    onBookService(serviceType);
  };

  if (!featuredProjects?.length) return null;

  const currentProject = featuredProjects?.[currentSlide];

  return (
    <div className="bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
            Featured Transformations
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Discover our most exceptional cleaning and flooring projects that showcase 
            the quality and attention to detail that sets Clean360 apart.
          </p>
        </div>

        <div className="relative">
          {/* Main Carousel */}
          <div className="relative h-96 lg:h-[500px] rounded-lg overflow-hidden shadow-elevation-3">
            <div 
              className="flex transition-transform duration-500 ease-in-out h-full"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {featuredProjects?.map((project, index) => (
                <div key={project?.id} className="w-full flex-shrink-0 relative">
                  <Image
                    src={project?.images?.[0]?.url}
                    alt={project?.title}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay Content */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                      <div className="max-w-4xl">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                            {project?.serviceTypeLabel}
                          </span>
                          <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">
                            {project?.location}
                          </span>
                        </div>
                        
                        <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                          {project?.title}
                        </h3>
                        
                        <p className="text-white/90 text-lg mb-4 max-w-2xl">
                          {project?.description}
                        </p>

                        <div className="flex items-center space-x-6 mb-6 text-white/80">
                          <div className="flex items-center space-x-2">
                            <Icon name="Clock" size={16} />
                            <span className="text-sm">{project?.duration}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Icon name="MapPin" size={16} />
                            <span className="text-sm">{project?.area}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Icon name="Calendar" size={16} />
                            <span className="text-sm">{project?.completionDate}</span>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                          <Button
                            variant="default"
                            onClick={() => handleViewProject(project)}
                            iconName="Eye"
                            iconPosition="left"
                          >
                            View Full Project
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleBookService(project?.serviceType)}
                            iconName="Calendar"
                            iconPosition="left"
                            className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-text-primary"
                          >
                            Book Similar Service
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {featuredProjects?.length > 1 && (
              <>
                <button
                  onClick={handlePrevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <Icon name="ChevronLeft" size={24} />
                </button>
                <button
                  onClick={handleNextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <Icon name="ChevronRight" size={24} />
                </button>
              </>
            )}

            {/* Auto-play Control */}
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <Icon name={isAutoPlaying ? "Pause" : "Play"} size={16} />
            </button>
          </div>

          {/* Slide Indicators */}
          {featuredProjects?.length > 1 && (
            <div className="flex justify-center space-x-2 mt-6">
              {featuredProjects?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleSlideClick(index)}
                  className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                    index === currentSlide ? 'bg-primary' : 'bg-border hover:bg-primary/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Testimonial Section */}
        {currentProject?.testimonial && (
          <div className="mt-8 bg-muted rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={currentProject?.testimonial?.customerAvatar}
                  alt={currentProject?.testimonial?.customerName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-1 mb-2">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={16}
                      color={i < currentProject?.testimonial?.rating ? "var(--color-warning)" : "var(--color-border)"}
                    />
                  ))}
                </div>
                <blockquote className="text-text-primary italic text-lg mb-3">
                  "{currentProject?.testimonial?.comment}"
                </blockquote>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-text-primary">
                      {currentProject?.testimonial?.customerName}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {currentProject?.testimonial?.customerTitle}
                    </p>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {currentProject?.completionDate}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedCarousel;